<template>
  <div class="d-flex" role="group" aria-label="Date range selection">
    <!-- From Date Field -->
    <div
      class="d-flex align-center"
      :class="{ error: error && error.hasError }"
      :style="{ paddingTop: label ? '20px' : '0' }"
    >
      <label v-if="labelIsVisible" :for="'Date from'" class="mr-4 date-label"> From </label>
      <FlatPickr
        :id="'Date from'"
        ref="fatPicker_from"
        v-model="setLocalDateFrom"
        name="nameFrom"
        :config="flatPicrConfigFrom"
        :placeholder="placeholder || 'Select start date'"
        :disabled="disabled"
        :class="{ error: error && error.hasError }"
        :aria-label="labelIsVisible ? undefined : 'From date'"
        :aria-describedby="error && error.hasError ? `error-from-${componentKey}` : undefined"
        @on-close="$emit('onClose')"
        @click="$emit('clearErrors')"
        @focus="toggleLabel(true)"
        @blur="toggleLabel(false)"
      />

      <transition name="fade">
        <HasError
          v-if="error && error.hasError"
          :id="`error-from-${componentKey}`"
          :key="`hasError-from-${componentKey}`"
          :error="error"
          role="alert"
          aria-live="polite"
        />
      </transition>
    </div>

    <!-- To Date Field -->
    <div
      class="d-flex align-center"
      :class="{ error: error && error.hasError }"
      :style="{ paddingTop: label ? '20px' : '0' }"
    >
      <label v-if="labelIsVisible" :for="'Date to'" class="mr-4 ml-4 date-label"> To </label>
      <FlatPickr
        :id="'Date to'"
        ref="fatPicker_to"
        v-model="setLocalDateTo"
        name="nameTo"
        :config="flatPicrConfigTo"
        :placeholder="placeholder || 'Select end date'"
        :disabled="disabled"
        :class="{ error: error && error.hasError }"
        :aria-label="labelIsVisible ? undefined : 'To date'"
        :aria-describedby="error && error.hasError ? `error-to-${componentKey}` : undefined"
        @on-close="$emit('onClose')"
        @click="$emit('clearErrors')"
        @focus="toggleLabel(true)"
        @blur="toggleLabel(false)"
      />
      <transition name="fade">
        <HasError v-if="error && error.hasError" :key="`hasError-${componentKey}`" :error="error" />
      </transition>
    </div>
  </div>
</template>

<script lang="ts">
  import { defineAsyncComponent, defineComponent } from 'vue'
  import type { PropType } from 'vue'
  import { CommonError } from '@/entities/errors/form_errors/CommonError'
  import type { DateOption, BaseOptions } from 'flatpickr/dist/types/options'
  import type { SelectedDateI } from '@/typescript/types/ui-components/DatePicker'

  export default defineComponent({
    name: 'ActivationDate',
    components: {
      FlatPickr: defineAsyncComponent(() => import('vue-flatpickr-component')),
    },
    props: {
      value: {
        type: Object as PropType<SelectedDateI>,
        required: true,
      },
      minActivationDate: {
        type: String as PropType<DateOption>,
        required: false,
      },
      maxActivationDate: {
        type: String as PropType<DateOption>,
        required: false,
      },
      name: {
        type: String,
        default: '',
      },
      label: {
        type: String,
        default: '',
      },
      placeholder: {
        type: String,
        default: '',
      },
      error: {
        type: CommonError,
        required: true,
      },
      disabled: Boolean,
      mode: {
        type: String as PropType<'single' | 'multiple' | 'range' | 'time'>,
        default: 'single',
      },
      isAlwaysShowLabel: {
        type: Boolean,
        default: false,
      },
    },
    emits: ['update:value', 'clearErrors', 'onClose'],
    data() {
      return {
        labelIsVisible: false,
        componentKey: 0,
      }
    },
    computed: {
      setLocalDateFrom: {
        get(): string {
          const date = this.value
          if (date?.dateFrom) {
            return date.dateFrom
          } else {
            return ''
          }
        },
        set(val: string) {
          const date = this.value
          date.dateFrom = val
          this.$emit('update:value', {
            date,
            picker: this.name,
          })
        },
      },
      setLocalDateTo: {
        get(): string {
          const date = this.value
          if (date?.dateTo) {
            return date.dateTo
          } else {
            return ''
          }
        },
        set(val: string) {
          const date = this.value
          date.dateTo = val
          this.$emit('update:value', {
            date,
            picker: this.name,
          })
        },
      },
      flatPicrConfigFrom(): Partial<BaseOptions> {
        return {
          dateFormat: 'Y-m-d',
          mode: this.mode,
        }
      },
      flatPicrConfigTo(): Partial<BaseOptions> {
        return {
          dateFormat: 'Y-m-d',
          mode: this.mode,
          ariaDateFormat: 'F j, Y',
        }
      },
    },
    async mounted() {
      await import('flatpickr/dist/flatpickr.css')

      if (this.isAlwaysShowLabel) {
        this.labelIsVisible = true
      }
    },
    methods: {
      dateFrom(dateRange: string) {
        if (dateRange && dateRange.includes('to')) {
          return dateRange.split(' to ')[0]
        } else if (dateRange) {
          return dateRange
        } else {
          return ''
        }
      },
      dateTo(dateRange: string) {
        return dateRange.split(' to ')[1]
      },
      clearError(name: any) {
        this.$emit('clearErrors', name)
        this.forceRerender()
      },
      forceRerender() {
        this.componentKey++
      },
      clearInput() {
        const fatPicker = this.$refs.fatPicker as any
        fatPicker.fp.clear()
      },
      toggleLabel(val: boolean) {
        if (!this.isAlwaysShowLabel) {
          this.labelIsVisible = val
        }
      },
    },
  })
</script>

<style scoped lang="scss">
  .error-wrap-label {
    display: block;
    position: relative;
    .tip-item {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      font-size: 12px;
    }
  }
  ::v-deep(input):not([type='submit']) {
    height: 43px;
    padding: 0 10px 0;
    border: 1px solid #777777;
    border-radius: 6px;
    background-color: #fff;
    color: #4e4e4e;
    box-sizing: border-box;
    position: relative;
    z-index: 3;
    width: 100%;
    transition: all 0.3s ease;
    font-family: var(--font-source-sans);
    &::-webkit-input-placeholder {
      color: var(--color-text-2);
      transition: color 0.2s ease;
    }
    &:focus,
    &:focus-within,
    &:focus-visible {
      outline: none;
      border-color: var(--color-link-blue);
      background-color: var(--color-background-input);
      &::-webkit-input-placeholder {
        color: transparent;
      }
    }
    &[disabled] {
      background-color: #fafafa;
    }
    &.error {
      border-color: var(--color-border-error);
      background-color: var(--color-background-error);
    }
  }
  .date-label {
    margin-left: 10px;
  }
  .flatpickr-input[disabled] {
    color: #b4b4b4;
    cursor: not-allowed;
  }
  .flatpickr-input {
    background: url('/src/assets/icons/calendar.svg') no-repeat right;
    background-size: 15px;
    background-position-x: 130px;
  }
</style>
